import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Heart, 
  Zap, 
  Moon, 
  TrendingUp, 
  Activity,
  Target,
  Lightbulb,
  Crown,
  ArrowLeft
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar,
  BarChart,
  Bar
} from 'recharts';
import StatCard from './StatCard';

const MentalDevelopmentStats = ({ isDarkMode = true, onBackToOverview }) => {
  const mentalData = {
    overallScore: 87,
    moodStability: 85,
    stressLevel: 25,
    mindfulnessScore: 92,
    sleepQuality: 88,
    weeklyProgress: [
      { day: 'Pzt', mood: 8, stress: 3, mindfulness: 7, sleep: 8 },
      { day: 'Sal', mood: 7, stress: 4, mindfulness: 8, sleep: 7 },
      { day: 'Çar', mood: 9, stress: 2, mindfulness: 9, sleep: 9 },
      { day: 'Per', mood: 6, stress: 6, mindfulness: 6, sleep: 6 },
      { day: 'Cum', mood: 8, stress: 3, mindfulness: 8, sleep: 8 },
      { day: 'Cmt', mood: 9, stress: 2, mindfulness: 9, sleep: 9 },
      { day: 'Paz', mood: 8, stress: 3, mindfulness: 7, sleep: 8 }
    ],
    mentalHealthActivities: [
      { name: 'Meditasyon', duration: 20, completed: true, xp: 15 },
      { name: 'Nefes Egzersizi', duration: 10, completed: true, xp: 8 },
      { name: 'Günlük Yazma', duration: 15, completed: false, xp: 12 },
      { name: 'Pozitif Düşünce', duration: 5, completed: true, xp: 5 }
    ],
    radarData: [
      { subject: 'Ruh Hali', A: 85, fullMark: 100 },
      { subject: 'Stres Yönetimi', A: 75, fullMark: 100 },
      { subject: 'Odaklanma', A: 92, fullMark: 100 },
      { subject: 'Uyku', A: 88, fullMark: 100 },
      { subject: 'Farkındalık', A: 92, fullMark: 100 },
      { subject: 'Duygusal Denge', A: 80, fullMark: 100 }
    ],
    weeklyXP: 340,
    streak: 12,
    achievements: [
      { name: 'Meditasyon Ustası', description: '30 gün düzenli meditasyon', progress: 85 },
      { name: 'Stres Savaşçısı', description: 'Stres seviyesini 7 gün düşük tut', progress: 100 },
      { name: 'Uyku Kralı', description: '14 gün kaliteli uyku', progress: 60 }
    ]
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div variants={itemVariants}>
          <button
            onClick={onBackToOverview}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}

      {/* Mental Health Score Overview */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-purple-900 to-indigo-900' : 'bg-gradient-to-br from-purple-50 to-indigo-50'} shadow-lg border-2 ${isDarkMode ? 'border-purple-500/20' : 'border-purple-200'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🧠 Mental Gelişim Skoru
              </h2>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Zihinsel sağlık ve gelişim durumunuz
              </p>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold text-purple-500 mb-2`}>
                {mentalData.overallScore}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                /100
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Cards */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Ruh Hali" 
            icon={<Heart />} 
            value={mentalData.moodStability} 
            unit="/100" 
            progress={mentalData.moodStability}
            trend={8}
            color="#00C2FF"
            isDarkMode={isDarkMode}
            index={0}
          />
          <StatCard 
            title="Stres Seviyesi" 
            icon={<Zap />} 
            value={mentalData.stressLevel} 
            unit="/100" 
            progress={100 - mentalData.stressLevel}
            trend={-12}
            color="#FF6B6B"
            isDarkMode={isDarkMode}
            index={1}
          />
          <StatCard 
            title="Farkındalık" 
            icon={<Lightbulb />} 
            value={mentalData.mindfulnessScore} 
            unit="/100" 
            progress={mentalData.mindfulnessScore}
            trend={15}
            color="#FFA400"
            isDarkMode={isDarkMode}
            index={2}
          />
          <StatCard 
            title="Uyku Kalitesi" 
            icon={<Crown />} 
            value={mentalData.sleepQuality} 
            unit="/100" 
            progress={mentalData.sleepQuality}
            trend={5}
            color="#00FFD1"
            isDarkMode={isDarkMode}
            index={3}
          />
        </div>
      </motion.div>

      {/* Weekly Progress and Radar Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Progress Chart */}
        <motion.div variants={itemVariants}>
          <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
              Haftalık Mental Sağlık İlerlemesi
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mentalData.weeklyProgress}>
                  <XAxis dataKey="day" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                  <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                      borderColor: isDarkMode ? '#374151' : '#E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="mood" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    name="Ruh Hali"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="mindfulness" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name="Farkındalık"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sleep" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    name="Uyku"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Radar Chart */}
        <motion.div variants={itemVariants}>
          <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Brain className="h-5 w-5 mr-2 text-purple-500" />
              Mental Sağlık Dengesi
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={mentalData.radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar
                    name="Skor"
                    dataKey="A"
                    stroke="#8B5CF6"
                    fill="#8B5CF6"
                    fillOpacity={0.3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mental Health Activities */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-green-500" />
            Bugünün Mental Sağlık Aktiviteleri
          </h3>
          <div className="space-y-3">
            {mentalData.mentalHealthActivities.map((activity, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                } ${activity.completed ? 'border-l-4 border-green-500' : ''}`}
              >
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    activity.completed ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {activity.name}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {activity.duration} dakika
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${
                    activity.completed ? 'text-green-500' : 'text-gray-400'
                  }`}>
                    +{activity.xp} XP
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Progress Summary */}
      <motion.div variants={itemVariants}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-purple-500 mb-2">
              {mentalData.weeklyXP}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Haftalık XP
            </p>
          </div>
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-orange-500 mb-2">
              {mentalData.streak}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Gün Seri
            </p>
          </div>
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg text-center`}>
            <div className="text-2xl font-bold text-green-500 mb-2">
              {mentalData.achievements.filter(a => a.progress === 100).length}
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Tamamlanan Başarım
            </p>
          </div>
        </div>
      </motion.div>

      {/* Achievements Progress */}
      <motion.div variants={itemVariants}>
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-amber-500" />
            Mental Sağlık Başarımları
          </h3>
          <div className="space-y-4">
            {mentalData.achievements.map((achievement, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {achievement.name}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {achievement.description}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${
                    achievement.progress === 100 ? 'text-green-500' : 'text-blue-500'
                  }`}>
                    {achievement.progress}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <motion.div
                    className={`h-full rounded-full ${
                      achievement.progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${achievement.progress}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MentalDevelopmentStats;
